import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { debounce } from "lodash";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSSegmentedTabs from "views/system-design/DSSegmentedTabs";
import PageHeader from "components/PageHeader";
import useTabParam from "components/hooks/useTabParam";
import { TicketsProvider, useTickets } from "../context/TicketsContext";
import { Ticket } from "../types";
import useUsers from "components/hooks/useUsers";
import firebaseService from "firebase_service/firebaseService";
import { useLocalTicketFilters } from "../hooks/useLocalTicketFilters";
import {
  getAssignableUsers,
  resolveUserId,
  buildTicketSearchParams,
} from "../utils";
import {
  useTicketSearch,
  useTicketFacets,
  useTicketById,
} from "../hooks/useTicketQueries";
import { useIsMobile } from "../hooks/useIsMobile";
import TicketsToolbar from "./TicketsToolbar";
import TicketFiltersPanel from "./TicketFilters";
import TicketsTable from "./table/TicketsTable";
import TicketsBoard from "./board/TicketsBoard";
import TicketDetailPanel from "./detail/TicketDetailPanel";
import TicketDetailModal from "./detail/TicketDetailModal";
import CreateTicketModal from "./detail/CreateTicketModal";
import LabelPanel from "./LabelPanel";
import LoadMoreFooter from "./shared/LoadMoreFooter";
import KlydeasView from "./klydeas/KlydeasView";
import ArchiveView from "./archive/ArchiveView";
import TicketsCalendarView from "./calendar/TicketsCalendarView";
import ReviewsView from "../reviews/components/ReviewsView";
import { usePendingReviewCount } from "../reviews/hooks/useReviewQueries";

function TicketsContent() {
  const { invalidate } = useTickets();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    filters,
    setFilters,
    showFilters,
    setShowFilters,
    selectedAssignees,
    setSelectedAssignees,
    onlyActive,
    setOnlyActive,
    onlyUnassigned,
    setOnlyUnassigned,
    selectedPanelLabels,
    setSelectedPanelLabels,
    showLabelPanel,
    setShowLabelPanel,
    viewMode,
    setViewMode,
    EMPTY_FILTERS,
  } = useLocalTicketFilters();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const selectTicket = useCallback(
    (ticket: Ticket | null) => {
      setSelectedTicket(ticket);
      if (ticket) invalidate();
    },
    [invalidate],
  );
  const [showCreateModal, setShowCreateModal] = useState(false);
  const showArchive = viewMode === "archive";
  const showCalendar = viewMode === "calendar" || viewMode === "weekly";
  const { data: allUsers } = useUsers();
  const currentUserId = firebaseService.getUserId() ?? "";
  const isMobile = useIsMobile();

  const users = allUsers ?? [];

  const searchParameters = useMemo(
    () =>
      buildTicketSearchParams({
        filters,
        searchQuery,
        selectedAssignees,
        selectedPanelLabels,
        users,
        isArchived: false,
        onlyActive,
        onlyUnassigned,
        excludeIdeas: true,
      }),
    [
      filters,
      searchQuery,
      selectedAssignees,
      selectedPanelLabels,
      users,
      onlyActive,
      onlyUnassigned,
    ],
  );

  const { tickets, total, loadedCount, isLoading, hasMore, loadMore } =
    useTicketSearch(searchParameters, { enabled: !showArchive });

  // Option lists and label progress describe the whole board, so they ignore
  // the active filters and the pages currently loaded.
  const facetFilters = useMemo(
    () => ({ isArchived: showArchive }),
    [showArchive],
  );
  const facets = useTicketFacets(facetFilters);

  const assignees = useMemo(() => {
    const assigneeIds = new Set(
      facets.assignees.map(({ value }) => resolveUserId(value, users)),
    );
    return getAssignableUsers(users).filter((user) => assigneeIds.has(user.id));
  }, [users, facets.assignees]);

  const handleToggleAssignee = useCallback((userId: string) => {
    setSelectedAssignees((prev) =>
      prev.includes(userId)
        ? prev.filter((assignee) => assignee !== userId)
        : [...prev, userId],
    );
  }, []);

  const debouncedSetSearch = useCallback(
    debounce((query: string) => setSearchQuery(query), 300),
    [],
  );

  useEffect(() => {
    return () => debouncedSetSearch.cancel();
  }, [debouncedSetSearch]);

  // Capture the ticket ID from the URL once, so the sync effect can't erase it
  const [initialTicketId] = useState(() => searchParams.get("ticket"));
  const didAutoSelect = useRef(false);

  // Auto-select the ticket named in the URL. It is fetched by id so the link
  // still opens when the ticket sits outside the active filters.
  const linkedTicket = useTicketById(initialTicketId);

  useEffect(() => {
    if (didAutoSelect.current || !linkedTicket) return;
    selectTicket(linkedTicket);
    didAutoSelect.current = true;
  }, [linkedTicket, selectTicket]);

  // Sync selected ticket → URL search param
  useEffect(() => {
    setSearchParams(
      (prev) => {
        if (selectedTicket) {
          prev.set("ticket", selectedTicket.id);
        } else {
          prev.delete("ticket");
        }
        return prev;
      },
      { replace: true },
    );
  }, [selectedTicket, setSearchParams]);

  const archiveSearchParameters = useMemo(
    () =>
      buildTicketSearchParams({
        filters,
        searchQuery,
        selectedAssignees,
        selectedPanelLabels,
        users,
        isArchived: true,
      }),
    [filters, searchQuery, selectedAssignees, selectedPanelLabels, users],
  );

  const handleClearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSelectedAssignees([]);
    setSelectedPanelLabels([]);
    setOnlyUnassigned(false);
    // "Only active" hides done and hold tickets, so clearing has to reset it
    setOnlyActive(false);
  };

  if (isLoading) {
    return (
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          p: 4,
        }}
      >
        <DSCircularProgress />
        <DSTypography sx={{ ml: 2 }}>Loading tickets...</DSTypography>
      </DSBox>
    );
  }

  return (
    <DSBox sx={{ height: "100%" }}>
      <TicketsToolbar
        viewMode={viewMode}
        onViewModeChange={(mode) => {
          setViewMode(mode);
          setSelectedTicket(null);
        }}
        onSearchChange={debouncedSetSearch}
        onToggleFilters={() => setShowFilters((prev) => !prev)}
        showFilters={showFilters}
        onCreateTicket={() => setShowCreateModal(true)}
        assignees={assignees}
        selectedAssignees={selectedAssignees}
        onToggleAssignee={handleToggleAssignee}
        showLabelPanel={showLabelPanel}
        onToggleLabelPanel={() => setShowLabelPanel((prev) => !prev)}
        onlyActive={onlyActive}
        onToggleOnlyActive={() => setOnlyActive((prev) => !prev)}
        onlyUnassigned={onlyUnassigned}
        onToggleOnlyUnassigned={() => setOnlyUnassigned((prev) => !prev)}
      />
      {showFilters && (
        <TicketFiltersPanel
          filters={filters}
          onFiltersChange={setFilters}
          onClearFilters={handleClearFilters}
          facets={facets}
        />
      )}
      <DSBox sx={{ display: "flex", mt: 2, flex: 1, overflow: "auto" }}>
        {showLabelPanel && !isMobile && !showCalendar && (
          <LabelPanel
            labelStats={facets.labelStats}
            unlabeledCount={facets.unlabeledCount}
            selectedLabels={selectedPanelLabels}
            onToggleLabel={(label) =>
              setSelectedPanelLabels((prev) =>
                prev.includes(label)
                  ? prev.filter((existing) => existing !== label)
                  : [...prev, label],
              )
            }
            onClose={() => setShowLabelPanel(false)}
          />
        )}
        <DSBox sx={{ flex: 1 }}>
          {showArchive ? (
            <ArchiveView searchParameters={archiveSearchParameters} />
          ) : showCalendar ? (
            <TicketsCalendarView
              tickets={tickets}
              onSelectTicket={selectTicket}
              viewMode={viewMode === "weekly" ? "week" : "month"}
              onViewModeChange={(calendarMode) =>
                setViewMode(calendarMode === "week" ? "weekly" : "calendar")
              }
            />
          ) : viewMode === "table" ? (
            <TicketsTable
              tickets={tickets}
              users={allUsers ?? []}
              onSelectTicket={(ticket) =>
                selectTicket(selectedTicket?.id === ticket.id ? null : ticket)
              }
              selectedTicketId={selectedTicket?.id}
            />
          ) : (
            <TicketsBoard
              tickets={tickets}
              onSelectTicket={selectTicket}
              currentUser={currentUserId}
              statusFilter={filters.statuses}
            />
          )}
          {!showArchive && (
            <LoadMoreFooter
              loadedCount={loadedCount}
              total={total}
              hasMore={hasMore}
              onLoadMore={loadMore}
            />
          )}
        </DSBox>
      </DSBox>
      {!showArchive &&
        (viewMode === "table" ? (
          <TicketDetailPanel
            ticket={selectedTicket}
            open={!!selectedTicket}
            onClose={() => setSelectedTicket(null)}
          />
        ) : (
          selectedTicket && (
            <TicketDetailModal
              ticket={selectedTicket}
              onClose={() => setSelectedTicket(null)}
            />
          )
        ))}
      {showCreateModal && (
        <CreateTicketModal onClose={() => setShowCreateModal(false)} />
      )}
    </DSBox>
  );
}

type KlydoscopeTab = "tickets" | "ideas" | "reviews";
const KLYDOSCOPE_TABS: KlydoscopeTab[] = ["tickets", "ideas", "reviews"];

function TicketsPageInner() {
  const [tabParam, setTabParam] = useTabParam(
    "tab",
    "tickets",
    KLYDOSCOPE_TABS,
  );
  const activeTab = (tabParam ?? "tickets") as KlydoscopeTab;
  const pendingReviews = usePendingReviewCount();

  return (
    <DSBox sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <PageHeader
        overline="Devices"
        title="Klydoscope"
        subtitle="Support tickets, ideas and PR reviews — triage, assign and track work across the team"
        tabs={
          <DSSegmentedTabs<KlydoscopeTab>
            options={[
              { label: "Tickets", value: "tickets" },
              { label: "Ideas", value: "ideas" },
              {
                label: pendingReviews
                  ? `Reviews (${pendingReviews})`
                  : "Reviews",
                value: "reviews",
              },
            ]}
            value={activeTab}
            onChange={(value) => setTabParam(value ?? "tickets")}
          />
        }
      />
      <DSBox sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        {activeTab === "tickets" ? (
          <TicketsContent />
        ) : activeTab === "ideas" ? (
          <KlydeasView />
        ) : (
          <ReviewsView />
        )}
      </DSBox>
    </DSBox>
  );
}

export default function TicketsPage() {
  return (
    <TicketsProvider>
      <TicketsPageInner />
    </TicketsProvider>
  );
}
